import React, { useEffect, useState } from 'react';
import { getUrl } from 'aws-amplify/storage';
import { Check, Loader2, X } from 'lucide-react';
import { client } from '../../lib/amplifyClient';
import {
  fetchTherapistProfileByUserId,
  shouldExposeTherapistProfileToParents,
} from '../../lib/therapistProfilePublic';
import { keyForAmplifyGetUrl } from '../../lib/storagePublicKey';

type Preview = {
  name: string;
  headline?: string | null;
  bio?: string | null;
  organizationName?: string | null;
  specialties: string[];
  ageRanges: string[];
  languages: string[];
  experienceYears?: number | null;
  sessionTypes: string[];
  sessionRateDisplay?: string | null;
  acceptsNewClients?: boolean | null;
  photoUrl?: string | null;
};

type Props = {
  therapistUserId: string | null;
  onClose: () => void;
  /** When set (e.g. from a pending referral), show Accept / Decline under the profile. */
  onAccept?: () => void;
  onDecline?: () => void;
  acting?: boolean;
  actionError?: string | null;
};

export default function ParentTherapistPreviewModal({
  therapistUserId,
  onClose,
  onAccept,
  onDecline,
  acting = false,
  actionError = null,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const showActions = Boolean(onAccept && onDecline);

  useEffect(() => {
    if (!therapistUserId) {
      setPreview(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);

    void (async () => {
      try {
        const userResp = await client.models.User.get({ id: therapistUserId }, { authMode: 'apiKey' });
        const user = userResp.data;
        if (!user) {
          if (!cancelled) setError('Therapist not found.');
          return;
        }

        let organizationName: string | null = null;
        if (user.organizationId) {
          try {
            const orgResp = await client.models.Organization.get(
              { id: user.organizationId },
              { authMode: 'apiKey' },
            );
            organizationName = orgResp.data?.name || null;
          } catch {
            /* optional */
          }
        }

        const tp = await fetchTherapistProfileByUserId(therapistUserId);
        const expose = Boolean(tp && shouldExposeTherapistProfileToParents(tp.visibility));
        const name = `${user.fName || ''} ${user.lName || ''}`.trim() || user.email || 'Therapist';

        let photoUrl: string | null = null;
        const photoKey = expose ? (tp?.profilePhotoKey && String(tp.profilePhotoKey).trim()) || null : null;
        if (photoKey) {
          try {
            const u = await getUrl({ key: keyForAmplifyGetUrl(photoKey) });
            photoUrl = u.url.toString();
          } catch {
            photoUrl = null;
          }
        }

        if (cancelled) return;
        setPreview({
          name,
          headline: expose ? tp?.headline?.trim() || null : null,
          bio: expose ? tp?.bio?.trim() || null : null,
          organizationName,
          specialties: expose ? (tp?.specialties || []).filter(Boolean) : [],
          ageRanges: expose ? (tp?.ageRanges || []).filter(Boolean) : [],
          languages: expose ? (tp?.languages || []).filter(Boolean) : [],
          experienceYears: expose && typeof tp?.experienceYears === 'number' ? tp.experienceYears : null,
          sessionTypes: expose ? (tp?.sessionTypes || []).filter(Boolean) : [],
          sessionRateDisplay: expose ? (tp?.sessionRateDisplay && String(tp.sessionRateDisplay).trim()) || null : null,
          acceptsNewClients: expose ? tp?.acceptsNewClients ?? null : null,
          photoUrl,
        });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [therapistUserId]);

  if (!therapistUserId) return null;

  const chip = (label: string) => (
    <span
      key={label}
      className="inline-flex rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-800 ring-1 ring-indigo-100"
    >
      {label}
    </span>
  );

  return (
    <div
      className="fixed inset-0 z-[1700] flex items-end justify-center bg-slate-900/45 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="parent-therapist-preview-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !acting) onClose();
      }}
    >
      <div
        className="relative max-h-[min(88vh,36rem)] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={acting}
          className="absolute right-3 top-3 rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        {loading ? (
          <p className="flex items-center gap-2 py-10 text-sm text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" aria-hidden />
            Loading profile…
          </p>
        ) : error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{error}</p>
        ) : preview ? (
          <div className="pr-6">
            <div className="flex items-start gap-3">
              {preview.photoUrl ? (
                <img
                  src={preview.photoUrl}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-indigo-50"
                />
              ) : (
                <div
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-lg font-semibold text-indigo-800"
                  aria-hidden
                >
                  {(preview.name.trim()[0] || 'T').toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <h2 id="parent-therapist-preview-title" className="text-lg font-semibold text-slate-900">
                  {preview.name}
                </h2>
                {preview.headline ? (
                  <p className="mt-0.5 text-sm text-slate-600">{preview.headline}</p>
                ) : null}
                {preview.organizationName ? (
                  <p className="mt-1 text-xs font-medium text-slate-500">{preview.organizationName}</p>
                ) : null}
              </div>
            </div>

            {preview.acceptsNewClients != null ? (
              <p className="mt-4 text-sm text-slate-700">
                {preview.acceptsNewClients ? 'Accepting new clients' : 'Not accepting new clients right now'}
              </p>
            ) : null}

            {preview.experienceYears != null ? (
              <p className="mt-2 text-sm text-slate-700">
                Experience: <span className="font-medium">{preview.experienceYears} years</span>
              </p>
            ) : null}

            {preview.bio ? (
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{preview.bio}</p>
            ) : (
              <p className="mt-3 text-sm text-slate-500">No public bio yet.</p>
            )}

            {preview.specialties.length > 0 ? (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Specialties</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">{preview.specialties.map(chip)}</div>
              </div>
            ) : null}

            {preview.ageRanges.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ages</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">{preview.ageRanges.map(chip)}</div>
              </div>
            ) : null}

            {preview.languages.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Languages</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">{preview.languages.map(chip)}</div>
              </div>
            ) : null}

            {preview.sessionTypes.length > 0 || preview.sessionRateDisplay ? (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sessions</p>
                {preview.sessionTypes.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">{preview.sessionTypes.map(chip)}</div>
                ) : null}
                {preview.sessionRateDisplay ? (
                  <p className="mt-1.5 text-sm text-slate-700">{preview.sessionRateDisplay}</p>
                ) : null}
              </div>
            ) : null}

            {showActions ? (
              <div className="mt-5 border-t border-slate-100 pt-4">
                {actionError ? (
                  <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                    {actionError}
                  </p>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    disabled={acting}
                    onClick={onAccept}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Accept
                  </button>
                  <button
                    type="button"
                    disabled={acting}
                    onClick={onDecline}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    Decline
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
